import { DevicePlatform } from '../../enums';

import {
    Category,
    INotificationSchedule,
    ISubscriptionSettingsTopic,
    IVcfGeofenceCreatePayload,
    IVcfGeofenceUpdatePayload,
    IVcfSpeedAlertCreatePayload,
    IVcfSpeedAlertUpdatePayload,
    TransactionHistoryResponseData,
} from './types';
import { VcfNotificationClient } from './VcfNotificationClient';

const allNavigationItems = [
    {
        name: 'weconnect.notifications.editAlerts.category1',
        link: '/geofence',
        category: Category.GEOFENCE,
    },
    {
        name: 'weconnect.notifications.editAlerts.category2',
        link: '/speedalert',
        category: Category.SPEEDALERT,
    },
    {
        name: 'weconnect.notifications.editAlerts.category3',
        link: '/theftalarm',
        category: Category.TRANSACTION_HISTORY_ANTITHEFT_ALERT,
    },
];

const sortMessages = (messages: any) => {
    return messages.sort((a: any, b: any) => {
        if (a.timestamp > b.timestamp) {
            return -1;
        }
        if (a.timestamp < b.timestamp) {
            return 1;
        }
        return 0;
    });
};

const limitMessages = (messages: any, limit?: number) => {
    if (!limit || messages.length <= limit) {
        return messages;
    }
    const result = [];
    for (let i = 0; i < limit; i++) {
        result.push(messages[i]);
    }
    return result;
};

export class VcfNotificationService {
    constructor(private readonly vcfNotificationClient: VcfNotificationClient) {}

    public calcTime = (time: string, direction: 'from' | 'to'): string | undefined => {

        const date = new Date();
        const offset = date.getTimezoneOffset(); // in minutes
        const hours = Number(time.split(':')[0]);
        const minutes = Number(time.split(':')[1]);

        date.setHours(hours);
        date.setMinutes(direction === 'from' ? minutes - offset : minutes + offset);

        return date.toLocaleString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    };

    public fromUtc = (time: string | null | undefined) => {
        if (!time) {
            return null;
        }
        return this.calcTime(time, 'from');
    };

    public toUtc = (time: string | null | undefined) => {
        return this.calcTime(time || '00:00', 'to');
    };

    public shiftDays = (recurringOn: INotificationSchedule['recurringOn'], startTime: string | undefined | null) => {
        if (!recurringOn) {
            return;
        }

        let dayShift = 0; // -1 = shift backwards; +1 = shift forward
        const currentDate = new Date();
        const offsetMinutes = currentDate.getTimezoneOffset();

        // shift if startTime is null or undefined
        if (!startTime) {
            if (offsetMinutes < 0) {
                dayShift = -1;
            }
            if (offsetMinutes > 0) {
                dayShift = 1;
            }
            if (offsetMinutes === 0) {
                return recurringOn;
            }
        } else {
            const offsetHours = offsetMinutes / 60;
            const hours = Number(startTime.split(':')[0]);
            if (hours + offsetHours < 0) {
                dayShift = -1;
            }
            if (hours + offsetHours >= 24) {
                dayShift = 1;
            }
            if (dayShift === 0) {
                return recurringOn;
            }
        }

        const arr = Object.entries(recurringOn).map(([day, enabled]) => ({
            day,
            enabled,
        }));

        const shiftedArr = arr.map((item, i) => ({
            ...item,
            enabled:
                dayShift === -1
                    ? arr[i === arr.length - 1 ? 0 : i + 1].enabled
                    : arr[i === 0 ? arr.length - 1 : i - 1].enabled,
        }));

        const result = shiftedArr.reduce((a, v) => ({ ...a, [v.day as string]: v.enabled }), {});

        return result;
    };

    public unshiftDays = (recurringOn: INotificationSchedule['recurringOn'], startTime: string | undefined | null) => {
        if (!recurringOn) {
            return;
        }

        let dayShift = 0; // -1 = shift backwards; +1 = shift forward
        const currentDate = new Date();
        const offsetMinutes = currentDate.getTimezoneOffset();

        // shift if startTime is null or undefined
        if (!startTime) {
            if (offsetMinutes < 0) {
                dayShift = 1;
            }
            if (offsetMinutes > 0) {
                dayShift = -1;
            }
            if (offsetMinutes === 0) {
                return recurringOn;
            }
        } else {
            const offsetHours = offsetMinutes / 60;
            const hours = Number(startTime.split(':')[0]);

            if (hours - offsetHours < 0) {
                dayShift = -1;
            }

            if (hours - offsetHours >= 24) {
                dayShift = 1;
            }

            if (dayShift === 0) {
                return recurringOn;
            }
        }
        const arr = Object.entries(recurringOn).map(([day, enabled]) => ({
            day,
            enabled,
        }));

        const shiftedArr = arr.map((item, i) => ({
            ...item,
            enabled:
                dayShift === -1
                    ? arr[i === arr.length - 1 ? 0 : i + 1].enabled
                    : arr[i === 0 ? arr.length - 1 : i - 1].enabled,
        }));

        const result = shiftedArr.reduce((a, v) => ({ ...a, [v.day as string]: v.enabled }), {});

        return result;
    };

    public async getEditableCategories(vin: string, devicePlatform: DevicePlatform) {
        const isWcar = devicePlatform === DevicePlatform.WCAR;
        try {
            const response = await this.vcfNotificationClient.getUserCapabilities(vin, isWcar);
            const allowedCategories = response.data.filter((item) => item.status.length === 0).map((item) => item.id);
            return allowedCategories;
        } catch (error: any) {
            console.log(error);
            throw new Error(error?.message);
        }
    }

    public async getTransactionHistory(vin: string, devicePlatform: DevicePlatform, limit?: number) {
        const isWcar = devicePlatform === DevicePlatform.WCAR;

        try {
            const response = await this.vcfNotificationClient.getTransactionHistory(vin, isWcar);

            return {
                messages: limitMessages(sortMessages(response.data.messages), limit),
                errors: response.data.errors,
            } as TransactionHistoryResponseData;
        } catch (error: any) {
            if (error.response?.data?.error.code === 4103) {
                return {
                    messages: [],
                    errors: [],
                    errorStatus: undefined,
                };
            }
            throw new Error('Something went wrong');
        }
    }

    public getEditTabNavigationItems(data: string[]) {
        const lowerCasedArr = data.map((item) => item.toLowerCase());

        return allNavigationItems.filter(({ category }) => lowerCasedArr.includes(category.toLowerCase()));
    }

    public showNotificationCardEditBtn(data: string[]) {
        return (
            data.includes(Category.GEOFENCE) ||
            data.includes(Category.SPEEDALERT) ||
            data.includes(Category.TRANSACTION_HISTORY_ANTITHEFT_ALERT)
        );
    }

    public async deleteTransactionHistory(
        vin: string,
        devicePlatform: DevicePlatform,
        category: 'speedalert' | 'geofence' | 'antitheftalarm',
    ) {
        const isWcar = devicePlatform === DevicePlatform.WCAR;
        await this.vcfNotificationClient.deleteTransactionHistory(vin, isWcar, category);
    }

    public async getGeofenceNotifications(vin: string, devicePlatform: DevicePlatform) {
        const isWcar = devicePlatform === DevicePlatform.WCAR;
        try {
            const response = await this.vcfNotificationClient.getGeofenceNotifications(vin, isWcar);
            const responseDataCalculatedFromUtc = {
                ...response.data,
                geofences: response.data.geofences.map((item) => ({
                    ...item,
                    schedule: !item.schedule ? null : {
                        ...item.schedule,
                        startTime: this.fromUtc(item.schedule?.startTime),
                        endTime: this.fromUtc(item.schedule?.endTime),
                        recurringOn: this.unshiftDays(item.schedule?.recurringOn, item.schedule?.startTime),
                    },
                })),
            };
            return responseDataCalculatedFromUtc;
        } catch (e: any) {
            throw new Error(e.message);
        }
    }

    public async createGeofenceNotification(
        vin: string,
        devicePlatform: DevicePlatform,
        payload: IVcfGeofenceCreatePayload,
    ) {
        const isWcar = devicePlatform === DevicePlatform.WCAR;

        const payloadCalculatedToUtc = {
            ...payload,
            geofence: {
                ...payload.geofence,
                schedule: !payload.geofence.schedule ? null : {
                    ...payload.geofence.schedule,
                    startTime: this.toUtc(payload.geofence.schedule?.startTime),
                    endTime: this.toUtc(payload.geofence.schedule?.endTime),
                    recurringOn: this.shiftDays(
                        payload.geofence.schedule?.recurringOn,
                        payload.geofence.schedule?.startTime,
                    ),
                },
            },
        };

        const response = await this.vcfNotificationClient.createGeofenceNotification(
            vin,
            isWcar,
            payloadCalculatedToUtc as IVcfGeofenceCreatePayload,
        );
        return response.data;
    }

    public async updateGeofenceNotification(
        vin: string,
        devicePlatform: DevicePlatform,
        payload: IVcfGeofenceUpdatePayload,
    ) {
        const isWcar = devicePlatform === DevicePlatform.WCAR;

        const payloadCalculatedToUtc = {
            ...payload,
            geofences: payload.geofences.map((item) => ({
                ...item,
                schedule: !item.schedule ? null : {
                    ...item.schedule,
                    startTime: this.toUtc(item.schedule?.startTime),
                    endTime: this.toUtc(item.schedule?.endTime),
                    recurringOn: this.shiftDays(item.schedule.recurringOn, item.schedule.startTime),
                },
            })),
        };

        const response = await this.vcfNotificationClient.updateGeofenceNotification(
            vin,
            isWcar,
            payloadCalculatedToUtc as IVcfGeofenceUpdatePayload,
        );
        return response.data;
    }

    public async deleteGeofenceNotification(vin: string, devicePlatform: DevicePlatform, id: any) {
        const isWcar = devicePlatform === DevicePlatform.WCAR;
        const response = await this.vcfNotificationClient.deleteGeofenceNotification(vin, isWcar, id);
        return response.data;
    }

    public async getSpeedAlertNotifications(vin: string, devicePlatform: DevicePlatform) {
        const isWcar = devicePlatform === DevicePlatform.WCAR;
        try {
            const response = await this.vcfNotificationClient.getSpeedAlertNotifications(vin, isWcar);
            const responseDataCalculatedFromUtc = {
                ...response.data,
                speedAlerts: response.data.speedAlerts.map((item) => ({
                    ...item,
                    schedule: !item.schedule ? null : {
                        ...item.schedule,
                        startTime: this.fromUtc(item.schedule?.startTime),
                        endTime: this.fromUtc(item.schedule?.endTime),
                        recurringOn: this.unshiftDays(item.schedule?.recurringOn, item.schedule?.startTime),
                    },
                })),
            };
            return responseDataCalculatedFromUtc;
        } catch (e: any) {
            if (e.response?.data?.error.code === 4103) {
                return {
                    carCapturedTimestamp: '123',
                    maxDefinitions: 10,
                    maxActiveDefinitions: 0,
                    speedAlerts: [],
                };
            }
            throw new Error(e.message);
        }
    }

    public async createSpeedAlertNotification(
        vin: string,
        devicePlatform: DevicePlatform,
        payload: IVcfSpeedAlertCreatePayload,
    ) {
        const isWcar = devicePlatform === DevicePlatform.WCAR;

        const payloadCalculatedToUtc = {
            ...payload,
            speedAlert: {
                ...payload.speedAlert,
                schedule: !payload.speedAlert.schedule ? null : {
                    ...payload.speedAlert.schedule,
                    startTime: this.toUtc(payload.speedAlert.schedule?.startTime),
                    endTime: this.toUtc(payload.speedAlert.schedule?.endTime),
                    recurringOn: this.shiftDays(
                        payload.speedAlert.schedule?.recurringOn,
                        payload.speedAlert.schedule?.startTime,
                    ),
                },
            },
        };

        const response = await this.vcfNotificationClient.createSpeedAlertNotification(
            vin,
            isWcar,
            payloadCalculatedToUtc as IVcfSpeedAlertCreatePayload,
        );
        return response.data;
    }

    public async updateSpeedAlertNotification(
        vin: string,
        devicePlatform: DevicePlatform,
        payload: IVcfSpeedAlertUpdatePayload,
    ) {
        const isWcar = devicePlatform === DevicePlatform.WCAR;

        const payloadCalculatedToUtc = {
            ...payload,
            speedAlerts: payload.speedAlerts.map((item) => ({
                ...item,
                schedule: !item.schedule ? null : {
                    ...item.schedule,
                    startTime: this.toUtc(item.schedule?.startTime),
                    endTime: this.toUtc(item.schedule?.endTime),
                    recurringOn: this.shiftDays(item.schedule?.recurringOn, item.schedule?.startTime),
                },
            })),
        };

        const response = await this.vcfNotificationClient.updateSpeedAlertNotification(
            vin,
            isWcar,
            payloadCalculatedToUtc as IVcfSpeedAlertUpdatePayload,
        );
        return response.data;
    }

    public async deleteSpeedAlertNotification(vin: string, devicePlatform: DevicePlatform, id: any) {
        const isWcar = devicePlatform === DevicePlatform.WCAR;
        const response = await this.vcfNotificationClient.deleteSpeedAlertNotification(vin, isWcar, id);
        return response.data;
    }

    public async getSubscriptionSettings(vin: string, devicePlatform: DevicePlatform) {
        const isWcar = devicePlatform === DevicePlatform.WCAR;
        const response = await this.vcfNotificationClient.getSubscriptionSettings(vin, isWcar);
        return response.data;
    }

    public async updateSubscriptionSettings(
        vin: string,
        devicePlatform: DevicePlatform,
        payload: { topics: ISubscriptionSettingsTopic[] },
    ) {
        const isWcar = devicePlatform === DevicePlatform.WCAR;
        await this.vcfNotificationClient.updateSubscriptionSettings(vin, isWcar, payload);
    }

    public async getNotificationConfigRequestStatus(
        vin: string,
        devicePlatform: DevicePlatform,
        category: 'speed' | 'geofence',
        requestId: string,
    ) {
        const isWcar = devicePlatform === DevicePlatform.WCAR;
        const response = await this.vcfNotificationClient.getNotificationConfigRequestStatus(
            vin,
            isWcar,
            category,
            requestId,
        );
        if (['delayed', 'in_progress'].includes(response.data.status)) {
            throw new Error('request was delayed or is in progress');
        }
        return response.data;
    }
}
